import React, { useState, createContext, useEffect } from "react";
import { axiosInstanceFast } from "@services/api/axiosInstance";
import SplashLoader from "@components/loaders/SplashLoader";
import { LicenseType } from "@components/common/CommonTypes";
import { FeatureKey } from "@utils/allFeatures";
import { LogIn, RotateCw } from "lucide-react";
import { Button } from "@components/ui/button";

declare global {
  interface Window {
    lsComponentList: FeatureKey[];
  }
}

type GeneralContextType = {
  theme: string;
  productType: LicenseType;
  isAdmin?: boolean;
  toggleTheme: () => void;
  permissions: string[];
  isAdminUser: boolean;
  enabledSubFeatures?: string[];
  enabledFeatures?: string[];
  isLsAgent?: boolean;
};

export const GeneralContext = createContext<GeneralContextType>({
  theme: "light",
  productType: "",
  isAdmin: false,
  toggleTheme: () => {},
  permissions: [],
  isAdminUser: false,
  enabledSubFeatures: [],
  isLsAgent: false,
});

const useProduct = () => {
  const cachedTheme = localStorage.getItem("ls__theme");
  const [theme, setTheme] = useState(cachedTheme || "dark");

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
    document.body.classList.remove("light");
    document.body.classList.remove("dark");
    document.body.classList.add(theme === "light" ? "dark" : "light");
    localStorage.setItem("ls__theme", theme === "light" ? "dark" : "light");
  };
  const [productType, setProductType] = useState<LicenseType>("");
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [compLoading, setCompLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [enabledSubFeatures, setIsPremiuPlugin] = useState<string[]>([]);
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);
  const [isLsAgent, setIsLsAgent] = useState(false);

  const updateTheme = (productType = "ndr") => {
    // if (cachedTheme) {
    //   setTheme(cachedTheme);
    //   document.body.classList.remove("light");
    //   document.body.classList.remove("dark");
    //   document.body.classList.add(cachedTheme == "light" ? "light" : "dark");
    // } else {
    const productSplit = productType.split(",");
    if (productSplit.includes("ndr")) {
      setTheme("dark");
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add("dark");
      localStorage.setItem("ls__theme", "dark");
    } else if (productSplit.includes("ddr")) {
      setTheme("light");
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add("light");
      localStorage.setItem("ls__theme", "light");
    } else if (productSplit.includes("itdr")) {
      setTheme("light");
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add("light");
      localStorage.setItem("ls__theme", "light");
    } else if (productSplit.includes("cmx")) {
      setTheme("light");
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add("light");
      localStorage.setItem("ls__theme", "light");
    } else {
      setTheme("dark");
      document.body.classList.remove("light");
      document.body.classList.remove("dark");
      document.body.classList.add("dark");
      localStorage.setItem("ls__theme", "dark");
    }
  };

  const getProductType = async () => {
    try {
      const response = await axiosInstanceFast.get("/system/license");
      // console.log(response, productType, response);
      if (
        response.data.data.isActive === false &&
        location.pathname.replaceAll("/", "") != "license"
      ) {
        setProductType("");
        location.href = "/license/";
        // location.href = "/license_old";
      } else {
        updateTheme(response.data.data.productType);
        setProductType(response.data.data.productType);
        setIsAdmin(response.data.data.isAdmin);
        setIsPremiuPlugin(response.data.data.enabledSubFeatures);
        setEnabledFeatures(response.data.data.enabledFeatures || []);
        const lsAgentEnabledRaw =
          response.data.data?.ls_agents?.is_enabled ??
          response.data.data?.isLsAgent ??
          false;
        const lsAgentEnabled =
          typeof lsAgentEnabledRaw === "string"
            ? lsAgentEnabledRaw.trim().toLowerCase() === "true"
            : Boolean(lsAgentEnabledRaw);
        setIsLsAgent(lsAgentEnabled);
        sessionStorage.setItem("lsProductType", response.data.data.productType);
      }
    } catch (error: any) {
      // TODO maybe we need to show a activate license popup or warning message saying no license found
      // contact support.
      console.log("error", error);
      const resStatus = error?.response?.status ?? 0;
      if (resStatus === 401 || resStatus === 403) {
        let loginUrl = "/login";
        if (location.pathname)
          loginUrl += "/?next=/" + location.pathname.replace("/", "");
        location.href = loginUrl;
      } else if (
        location.pathname.replaceAll("/", "") != "license" &&
        resStatus === 400 &&
        error?.response?.data?.status == 0 &&
        error?.response?.data?.message == "license not found"
      ) {
        // If the license is not found or invalid, redirect to license page
        setProductType("");
        location.href = "/license/";
      }
    } finally {
      setTimeout(() => {
        setLicenseLoading(false);
      }, 200);
      // console.log("i fired once, theme", productType);
    }
  };

  const getComponentList = async () => {
    let compList = [];
    try {
      const now = new Date().getTime();
      // const lsComponentList = sessionStorage.getItem("lsComponentList");
      // const twoHours = 2 * 60 * 60 * 1000;
      // if (lsComponentList) {
      //   const parsedData = JSON.parse(lsComponentList);
      //   if (parsedData.timestamp && now - parsedData.timestamp < twoHours) {
      //     compList = parsedData.data;
      //     window.lsComponentList = compList;
      //     return;
      //   }
      // }
      const componentListResp = await axiosInstanceFast.get(
        "/system/component-list",
      );
      compList = componentListResp.data;
      window.lsComponentList = compList;
      const dataToStore = {
        data: compList,
        timestamp: now,
      };
      sessionStorage.setItem("lsComponentList", JSON.stringify(dataToStore));
      setCompLoading(false);
    } catch (e) {
      window.lsComponentList = compList;
      setCompLoading(false);
    }
  };

  const getPermissions = async () => {
    const permissionsdata = await axiosInstanceFast.get(
      "/system/user/permission/",
    );
    let permissions: string[] = [];
    if (permissionsdata.data.data) {
      permissions = permissionsdata.data.data.permissions;
    }

    if (permissionsdata.data.meta_flag) {
      setIsAdminUser(true);
    }

    setPermissions(permissions);
  };

  useEffect(() => {
    getProductType();
    getComponentList();
    getPermissions();
    // console.log("i fired once, here")
  }, []);
  return {
    productType,
    licenseLoading,
    compLoading,
    isAdmin,
    toggleTheme,
    theme,
    permissions,
    isAdminUser,
    enabledSubFeatures,
    enabledFeatures,
    isLsAgent,
  };
};

export const GeneralProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    productType,
    licenseLoading,
    compLoading,
    toggleTheme,
    theme,
    permissions,
    isAdminUser,
    enabledSubFeatures,
    enabledFeatures,
    isLsAgent,
  } = useProduct();

  if (licenseLoading || compLoading) {
    return <SplashLoader />;
  }

  if (!productType && location.pathname.replaceAll("/", "") != "license") {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex items-center border py-6 rounded-xl shadow-xl">
          <div className="text-[4rem] animate-pulse w-[140px] flex justify-center items-center h-[140px]">
            :(
          </div>
          <div className="flex h-[80px] flex-col justify-between">
            <div className="text-lg font-bold">
              Something went wrong. Error fetching license.
            </div>
            <div className="w-[30vw] text-xs mb-6">
              ⚠️ Unable to verify license. This might be due to a weak network
              connection. Please try again and reload the page.
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => location.reload()}>
                <span>Try Again</span>
                <RotateCw className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => (location.href = "/login")}
              >
                <span>Login Page</span>
                <LogIn className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GeneralContext.Provider
      value={{
        theme,
        productType,
        toggleTheme,
        permissions,
        isAdminUser,
        enabledSubFeatures,
        enabledFeatures,
        isLsAgent,
      }}
    >
      {children}
    </GeneralContext.Provider>
  );
};
